import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import BusinessPhoneNumberInput from '../form-inputs/business-phone-number-input/BusinessPhoneNumberInput';
import BrushIcon from '../../../assets/svg/brush.svg?react';

import CircularProgress from '@mui/material/CircularProgress';
import { useEmploymentCardForm } from './useEmploymentCardForm';
import styles from './EmploymentCard.module.scss';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import Tooltip from '@mui/material/Tooltip';
// import { SocialLink } from '../social-media-form/SocialMediaForm';
import { Employee } from '../../models/employee.model';
import { BadgeOutlined } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

type EmploymentCardProps = {
  simplified?: boolean;
  employee: Partial<Employee>;
};

export default function EmploymentCard({ simplified, employee }: EmploymentCardProps) {
  const { t } = useTranslation();
  const isLoading = false;
  const {
    formData,
    handleChange,
    saveChanges,
    isEdited,
    toggleEditing,
    formRef,
    showModal,
    confirmExit,
    cancelChanges,
  } = useEmploymentCardForm(employee);

  return (
    <Grid component='div' container className='inputs-editable'>
      <Grid component='div' size={{ xs: 12 }}>
        <Card className={`${styles.card} ${isEdited ? styles.card_border : ''}`} ref={formRef}>
          <CardContent className={isLoading ? styles.card_content_min_height : ''}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <>
                <Grid
                  component='div'
                  container
                  style={{
                    alignItems: 'center',
                    paddingTop: '0 !important',
                    paddingLeft: '0 !important',
                    paddingRight: '0 !important',
                    paddingBottom: '16px !important',
                    minHeight: '60px',
                    flexWrap: 'nowrap',
                    padding: '8px',
                  }}
                >
                  <Grid
                    component='div'
                    size={{ xs: 11 }}
                    container
                    style={{ alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}
                  >
                    <Grid
                      component='div'
                      sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                      <BadgeOutlined />
                    </Grid>
                    <Grid component='div'>
                      <Typography variant='h6'>{t('settings.titles.employment')}</Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    component='div'
                    size={{ xs: 1 }}
                    container
                    style={{ justifyContent: 'flex-end' }}
                  >
                    {!isEdited ? (
                      <Tooltip title={t('buttons.edit')} placement='top'>
                        <IconButton
                          id='employmentCardEditIconBtn'
                          onClick={toggleEditing}
                          sx={{ flexShrink: 0 }}
                        >
                          <BrushIcon id='employmentCardEditIconBtnBrushIcon' />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Button
                          id='employmentCardCancelBtn'
                          type='button'
                          className='btn-default btn-action-basic btn-cancel'
                          role='button'
                          variant='contained'
                          size='large'
                          onClick={cancelChanges}
                        >
                          {t('buttons.cancel')}
                        </Button>
                        <Button
                          id='employmentCardSaveBtn'
                          type='submit'
                          className='btn-default btn-action-basic'
                          role='button'
                          disabled={!isEdited}
                          variant='contained'
                          size='large'
                          onClick={saveChanges}
                        >
                          {t('buttons.save')}
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>

                {!simplified ? (
                  <Grid component='div' container spacing={2}>
                    <Grid component='div' size={{ xs: 12 }}>
                      <Grid component='div' container spacing={2}>
                        <Grid component='div' size={{ xs: 12, sm: 6 }}>
                          {' '}
                          <TextField
                            fullWidth
                            value={formData.employee_identification_number ?? ''}
                            label={t('labels.employeeId')}
                            onChange={(e) => {
                              handleChange('employee_identification_number')(e.target.value);
                            }}
                            disabled={!isEdited}
                          />
                        </Grid>
                        <Grid component='div' size={{ xs: 12, sm: 6 }}>
                          <BusinessPhoneNumberInput
                            value={formData.employee_tax_number}
                            onChange={handleChange('employee_tax_number')}
                            label={t('labels.employeeTaxNumber')}
                            disabled={!isEdited}
                          />
                        </Grid>
                        <Grid component='div' size={{ xs: 12, sm: 6 }}>
                          {' '}
                          <TextField
                            fullWidth
                            value={formData.position_title || ''}
                            onChange={(e) => {
                              handleChange('position_title')(e.target.value);
                            }}
                            disabled={!isEdited}
                            label={t('labels.positionTitle')}
                          />
                        </Grid>
                        <Grid component='div' size={{ xs: 12, sm: 6 }}>
                          {' '}
                          <TextField
                            fullWidth
                            value={formData.department ?? ''}
                            label={t('labels.department')}
                            onChange={(e) => {
                              handleChange('department')(e.target.value);
                            }}
                            disabled={!isEdited}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid component='div' size={{ xs: 12 }}>
                      <Grid component='div' container spacing={2}>
                        <Grid component='div' size={{ xs: 12 }}>
                          <Grid component='div' container spacing={2}>
                            <Grid component='div' size={{ xs: 12, sm: 6 }}>
                              {' '}
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  label={t('labels.startDate')}
                                  value={formData.start_date ? dayjs(formData.start_date) : null}
                                  onChange={(newValue) => {
                                    const formattedDate = newValue
                                      ? newValue.format('YYYY-MM-DD')
                                      : null;
                                    handleChange('start_date')(formattedDate);
                                  }}
                                  disabled={!isEdited}
                                  format='DD/MM/YYYY'
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid component='div' size={{ xs: 12, sm: 6 }}>
                              {' '}
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  label={t('labels.endDate')}
                                  value={formData.end_date ? dayjs(formData.end_date) : null}
                                  onChange={(newValue) => {
                                    const formattedDate = newValue
                                      ? newValue.format('YYYY-MM-DD')
                                      : null;
                                    handleChange('end_date')(formattedDate);
                                  }}
                                  disabled={!isEdited}
                                  format='DD/MM/YYYY'
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid component='div' size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        value={formData.position_description ?? ''}
                        label={t('labels.positionDescription')}
                        onChange={(e) => {
                          handleChange('position_description')(e.target.value);
                        }}
                        disabled={!isEdited}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid component='div' container spacing={2}>
                    <Grid component='div' size={{ xs: 12 }}>
                      <Grid component='div' container spacing={2}>
                        <Grid component='div' size={{ xs: 12, sm: 6 }}>
                          {' '}
                          <TextField
                            value={formData.position_title}
                            onChange={handleChange('position_title')}
                            disabled={!isEdited}
                            label={t('labels.positionTitle')}
                          />
                        </Grid>
                        <Grid component='div' size={{ xs: 12, sm: 6 }}>
                          {' '}
                          <TextField
                            fullWidth
                            value={formData.department ?? ''}
                            label={t('labels.department')}
                            onChange={(e) => {
                              handleChange('department')(e.target.value);
                            }}
                            disabled={!isEdited}
                          />
                        </Grid>
                        <Grid component='div' size={{ xs: 12, sm: 6 }}>
                          {' '}
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              label={t('labels.startDate')}
                              value={formData.start_date ? dayjs(formData.start_date) : null}
                              onChange={(newValue) => {
                                const formattedDate = newValue
                                  ? newValue.format('YYYY-MM-DD')
                                  : null;
                                handleChange('start_date')(formattedDate);
                              }}
                              disabled={!isEdited}
                              format='DD/MM/YYYY'
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Grid>

                        <Grid component='div' size={{ xs: 12, sm: 6 }}>
                          {' '}
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              label={t('labels.endDate')}
                              value={formData.end_date ? dayjs(formData.end_date) : null}
                              onChange={(newValue) => {
                                const formattedDate = newValue
                                  ? newValue.format('YYYY-MM-DD')
                                  : null;
                                handleChange('end_date')(formattedDate);
                              }}
                              disabled={!isEdited}
                              format='DD/MM/YYYY'
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Grid>
                        <Grid component='div' size={{ xs: 12 }}>
                          <TextField
                            fullWidth
                            value={formData.position_description ?? ''}
                            label={t('labels.positionDescription')}
                            onChange={(e) => {
                              handleChange('position_description')(e.target.value);
                            }}
                            disabled={!isEdited}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {
                  <Grid
                    component='div'
                    container
                    className={styles.container_margin_top}
                    style={{ alignItems: 'center' }}
                  >
                    <Grid component='div'>
                      {/* <Tooltip title={t('businessProfile.addSocials')} placement='top'>
                        <IconButton
                          onClick={toogleEditingSocialModal}
                          className={styles.add_social_button}
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </Tooltip> */}
                    </Grid>
                  </Grid>
                }
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
      <ConfirmModal open={showModal} onClose={saveChanges} onConfirm={confirmExit} />
      {/* <AddSocialsModal
        open={showAddSocialModal}
        onClose={saveChangesSocialForm}
        onConfirm={toogleEditingSocialModal}
      /> */}
    </Grid>
  );
}
